import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  Chip,
  TextField,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material'
import {
  AdminPanelSettings,
  CheckCircle,
  Cancel,
  DoneAll,
  ExpandMore,
  ExpandLess,
  AccessTime,
  HourglassBottom,
  Chat as ChatIcon,
} from '@mui/icons-material'
import { moderatorApprovalsApi } from '@api/endpoints/moderatorApprovals'
import { chatApi } from '@api/endpoints/chat'
import { useChatStore } from '@store/chatStore'
import type { ApprovalRequestDto, WorkflowApprovalNodeGroupDto } from '@api/types/moderatorApprovals'

export default function ModeratorApprovalsPage() {
  const queryClient = useQueryClient()

  const {
    data: groups = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['moderatorApprovals'],
    queryFn: moderatorApprovalsApi.getPending,
  })

  const totalPending = groups.reduce((sum, g) => sum + g.requests.length, 0)

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['moderatorApprovals'] })
    queryClient.invalidateQueries({ queryKey: ['userNotices'] })
    queryClient.invalidateQueries({ queryKey: ['unreadCount'] })
  }

  const approveMutation = useMutation({
    mutationFn: ({ requestId, notes }: { requestId: string; notes?: string }) =>
      moderatorApprovalsApi.approve(requestId, notes),
    onSuccess: invalidateAll,
  })

  const denyMutation = useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason?: string }) =>
      moderatorApprovalsApi.deny(requestId, reason),
    onSuccess: invalidateAll,
  })

  const bulkApproveMutation = useMutation({
    mutationFn: ({ adventureId, nodeId, requestIds, notes }: {
      adventureId: string; nodeId: string; requestIds?: string[]; notes?: string
    }) => moderatorApprovalsApi.bulkApprove(adventureId, nodeId, requestIds, notes),
    onSuccess: invalidateAll,
  })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <AdminPanelSettings sx={{ fontSize: 32, color: 'secondary.main' }} />
        <Typography variant="h4" fontWeight="bold">
          Moderator Approvals
        </Typography>
        {totalPending > 0 && (
          <Chip
            label={`${totalPending} pending`}
            color="secondary"
            size="small"
          />
        )}
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load approvals: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      )}

      {groups.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.4 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No pending approvals
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When participants reach an approval checkpoint, their requests will appear here.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groups.map((group) => (
            <ApprovalGroup
              key={`${group.adventureId}-${group.nodeId}`}
              group={group}
              onApprove={(requestId, notes) => approveMutation.mutate({ requestId, notes })}
              onDeny={(requestId, reason) => denyMutation.mutate({ requestId, reason })}
              onBulkApprove={(notes) =>
                bulkApproveMutation.mutate({
                  adventureId: group.adventureId,
                  nodeId: group.nodeId,
                  notes,
                })
              }
              isLoading={approveMutation.isPending || denyMutation.isPending || bulkApproveMutation.isPending}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

interface ApprovalGroupProps {
  group: WorkflowApprovalNodeGroupDto
  onApprove: (requestId: string, notes?: string) => void
  onDeny: (requestId: string, reason?: string) => void
  onBulkApprove: (notes?: string) => void
  isLoading: boolean
}

function ApprovalGroup({ group, onApprove, onDeny, onBulkApprove, isLoading }: ApprovalGroupProps) {
  const [expanded, setExpanded] = useState(true)
  const [bulkNotes, setBulkNotes] = useState('')
  const [showBulkNotes, setShowBulkNotes] = useState(false)

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      {/* Group Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          bgcolor: 'action.hover',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight="bold" noWrap>
            {group.adventureName}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {group.nodeLabel || group.nodeId} — {group.requests.length} request{group.requests.length !== 1 ? 's' : ''}
          </Typography>
          {group.instructions && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {group.instructions}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {group.requests.length > 1 && (
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<DoneAll />}
              disabled={isLoading}
              onClick={(e) => {
                e.stopPropagation()
                if (showBulkNotes && bulkNotes.trim()) {
                  onBulkApprove(bulkNotes.trim())
                  setBulkNotes('')
                  setShowBulkNotes(false)
                } else if (showBulkNotes) {
                  onBulkApprove()
                  setShowBulkNotes(false)
                } else {
                  setShowBulkNotes(true)
                  setExpanded(true)
                }
              }}
            >
              Approve All
            </Button>
          )}
          <IconButton size="small">
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        {/* Bulk Notes Field */}
        {showBulkNotes && (
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Optional notes for bulk approval..."
              value={bulkNotes}
              onChange={(e) => setBulkNotes(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => {
                onBulkApprove(bulkNotes.trim() || undefined)
                setBulkNotes('')
                setShowBulkNotes(false)
              }}
              disabled={isLoading}
            >
              Confirm
            </Button>
            <Button
              size="small"
              onClick={() => { setShowBulkNotes(false); setBulkNotes('') }}
            >
              Cancel
            </Button>
          </Box>
        )}

        {/* Individual Requests */}
        {group.requests.map((request, index) => (
          <Box key={request.id}>
            {index > 0 && <Divider />}
            <ApprovalRequestRow
              request={request}
              onApprove={onApprove}
              onDeny={onDeny}
              isLoading={isLoading}
            />
          </Box>
        ))}
      </Collapse>
    </Paper>
  )
}

interface ApprovalRequestRowProps {
  request: ApprovalRequestDto
  onApprove: (requestId: string, notes?: string) => void
  onDeny: (requestId: string, reason?: string) => void
  isLoading: boolean
}

function ApprovalRequestRow({ request, onApprove, onDeny, isLoading }: ApprovalRequestRowProps) {
  const [showDenyReason, setShowDenyReason] = useState(false)
  const [denyReason, setDenyReason] = useState('')
  const [showApproveNotes, setShowApproveNotes] = useState(false)
  const [approveNotes, setApproveNotes] = useState('')
  const [isOpeningChat, setIsOpeningChat] = useState(false)
  const { addPopout } = useChatStore()

  const handleChat = async () => {
    if (isOpeningChat) return
    setIsOpeningChat(true)
    try {
      const conversation = await chatApi.createDirectConversation({ recipientId: request.requestingUser.id })
      addPopout(conversation.id)
    } catch (err) {
      console.error('Failed to open chat:', err)
    } finally {
      setIsOpeningChat(false)
    }
  }

  const waitingTime = getWaitingTime(request.createdAt)
  const expiryCountdown = request.expiresAt ? getExpiryCountdown(request.expiresAt) : null

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar
          src={request.requestingUser.avatar}
          sx={{ width: 36, height: 36 }}
        >
          {request.requestingUser.displayName?.[0]?.toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600}>
            {request.requestingUser.displayName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <AccessTime sx={{ fontSize: 14 }} /> {waitingTime}
            </Typography>
            {expiryCountdown && (
              <Typography
                variant="caption"
                color={expiryCountdown.urgent ? 'error.main' : 'text.secondary'}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}
              >
                <HourglassBottom sx={{ fontSize: 14 }} /> {expiryCountdown.text}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Tooltip title="Chat with participant">
            <IconButton
              size="small"
              color="primary"
              onClick={handleChat}
              disabled={isOpeningChat}
            >
              {isOpeningChat ? <CircularProgress size={18} /> : <ChatIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<CheckCircle />}
            disabled={isLoading}
            onClick={() => {
              if (showApproveNotes && approveNotes.trim()) {
                onApprove(request.id, approveNotes.trim())
              } else if (showApproveNotes) {
                onApprove(request.id)
              } else {
                onApprove(request.id)
              }
            }}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<Cancel />}
            disabled={isLoading}
            onClick={() => {
              if (showDenyReason) {
                onDeny(request.id, denyReason.trim() || undefined)
                setDenyReason('')
                setShowDenyReason(false)
              } else {
                setShowDenyReason(true)
              }
            }}
          >
            Deny
          </Button>
        </Box>
      </Box>

      {/* Deny Reason Field */}
      <Collapse in={showDenyReason}>
        <Box sx={{ display: 'flex', gap: 1, ml: 6.5 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Optional reason for denial..."
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
          />
          <Button
            size="small"
            color="error"
            variant="contained"
            onClick={() => {
              onDeny(request.id, denyReason.trim() || undefined)
              setDenyReason('')
              setShowDenyReason(false)
            }}
            disabled={isLoading}
          >
            Confirm
          </Button>
          <Button
            size="small"
            onClick={() => { setShowDenyReason(false); setDenyReason('') }}
          >
            Cancel
          </Button>
        </Box>
      </Collapse>
    </Box>
  )
}

function getWaitingTime(createdAt: string): string {
  const now = new Date()
  const created = new Date(createdAt)
  const diffMs = now.getTime() - created.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m waiting`
  if (diffHours < 24) return `${diffHours}h ${diffMins % 60}m waiting`
  return `${Math.floor(diffHours / 24)}d ${diffHours % 24}h waiting`
}

function getExpiryCountdown(expiresAt: string): { text: string; urgent: boolean } | null {
  const now = new Date()
  const expires = new Date(expiresAt)
  const diffMs = expires.getTime() - now.getTime()

  if (diffMs <= 0) return { text: 'Expired', urgent: true }

  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)

  if (diffMins < 60) return { text: `${diffMins}m left`, urgent: diffMins < 10 }
  if (diffHours < 24) return { text: `${diffHours}h ${diffMins % 60}m left`, urgent: diffHours < 1 }
  return { text: `${Math.floor(diffHours / 24)}d left`, urgent: false }
}
